/**
 * @file usePublicConfig.ts
 * @brief React access to GET /api/public/config (B23): `link(key, fallback)`
 *
 * @details
 * Until the config arrives, and whenever it can't (backend before B23, an
 * error), every value is the caller's fallback: today's link, so the page
 * looks the same as before. See publicConfig.ts.
 *
 * @author DES (prepared with Claude)
 * @date 24-10-2026
 */

import { useQuery } from "@tanstack/react-query";
import { env } from "@/shared/config/env";
import {
  configValue,
  loadPublicConfig,
  optionalValue,
  publicConfigSnapshot,
  type PublicConfig,
} from "./publicConfig";

export const publicConfigKey = ["public", "config"] as const;

export function usePublicConfig() {
  const query = useQuery({
    queryKey: publicConfigKey,
    queryFn: () => loadPublicConfig(env.apiBaseUrl),
    placeholderData: () => publicConfigSnapshot() ?? undefined,
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const config: PublicConfig | null = query.data ?? null;
  return {
    config,
    /** A required link or address: the saved value, else `fallback`. */
    value: (key: string, fallback: string) => configValue(config, key, fallback),
    /** An optional one: "" = hidden; `fallback` only while there is no config. */
    optional: (key: string, fallback = "") => optionalValue(config, key, fallback),
  };
}
